<template>
   <question-wrapper :questionId='questionId'>

        <loader v-if='showLoader' :active.sync='showLoader' :can-cancel='false' :is-full-page='false'/>

        <question-header :title='title' />
        <question-text :explainer='explainer' :pass='pass' :fail='fail' :skip='skip' :callToAction='callToAction' v-on:doTest='startTests' />

        <div class='result'>
          <pass-message :pass='pass' :nextPage='nextPage' :previousPage='previousPage'>
            <div slot='message'>
              <p>
                <translate>Good news!</translate>&nbsp;
                <translate>Your connection speed is sufficient to connect to your class.</translate>&nbsp;
                <translate>Remember, your network connection can change over the course of your class, which may result in loss of video / audio. We recommend that you choose a quiet place with good internet to take your class.
                </translate>
              </p>
            </div>
          </pass-message>

			<!-- Fail -->
      		<div class='fail' v-if='fail'>

            <p v-if='!connectionSpeedPass'>
              <translate>It looks like you're working via a slow internet connection.</translate>&nbsp;
              <translate>This may disrupt your experience or prevent you from connecting.</translate>
            </p>

            <p v-if='!awsSocketPass'>
              <translate>It looks like you cannot connect to our real time messaging server.</translate>&nbsp;
              <translate>This will prevent you from sending and receiving chat messages and whiteboard updates.</translate>
            </p>

            <p v-if='!agoraSocketPass'>
              <translate>It looks like you cannot connect to our media server.</translate>&nbsp;
              <translate>This could prevent your webcam from connecting with others in your class.</translate>
            </p>

            <div class='supported-browsers'>
                <h3 class='text-center'><translate>Fix This</translate></h3>
                <ul v-if='!connectionSpeedPass'>
                    <li><translate>Check your router settings to make sure you're connected. Restarting often helps.</translate></li>
                    <li><translate>Turn off other app services that may be using your bandwidth, such as Spotify, YouTube or Netflix.</translate></li>
                    <li><translate>Contact your Internet Service Provider.</translate></li>
                </ul>

                <ul v-if='!awsSocketPass'>
                    <li><translate>Check your router settings to make sure you're connected. Restarting often helps.</translate></li>
                    <li><translate>Check your firewall settings. Our real time messaging system connects to a socket server, which must be accessible by our app.</translate></li>
                  <li><translate>More details available here.</translate>&nbsp;<a v-translate href="https://docs.google.com/document/d/1qXHRpor11OY3AEMKsVGrsvOCwDvkfXCfFmxh8FHBP_0/edit#heading=h.62g2kr74hkzs" target="_blank">Firewall Settings</a></li>
                </ul>

                <ul v-if='!agoraSocketPass'>
                    <li><translate>Check your router settings to make sure you're connected. Restarting often helps.</translate></li>
                    <li><translate>Check your firewall settings. Our media servers send traffic through some non-standard ports that need to be opened.</translate></li>
                    <li><translate>More details available here.</translate>&nbsp;<a v-translate href="https://docs.google.com/document/d/1qXHRpor11OY3AEMKsVGrsvOCwDvkfXCfFmxh8FHBP_0/edit#heading=h.62g2kr74hkzs" target="_blank">Firewall Settings</a></li>
                </ul>
            </div>

      		</div>
        </div>
  	</question-wrapper>
</template>

<script>
  import Loader from 'vue-loading-overlay'
	import BasePage from './common/BasePage.vue'
  import AgoraMixin from '@/../../shared/src/mixins/AgoraMixin'
  import { getOrCreateClient } from '@/../../shared/src/utils/agora.js'

	export default {

		name: 'Connection',

    mixins: [BasePage, AgoraMixin],

    components: {
		  loader: Loader
    },

		data () {
			return {
        vuexModule: 'connection',
        client: getOrCreateClient('webcam', 'rtc'),
        connectionSpeedPass: undefined,
        awsSocketPass: undefined,
        agoraSocketPass: undefined,
        twilioSocketPass: undefined,
        showLoader: false
      }
		},

    computed: {
		  allTests () {
		    return [this.connectionSpeedPass, this.awsSocketPass, this.agoraSocketPass, this.twilioSocketPass]
      }
    },

    watch: {
		  allTests () {
        const passed = this.allTests.filter(t => t)
        if (passed.length === 4) {
          this.passTest()
          this.showLoader = false
        }
      }
    },

		methods: {

      /**
      * _*Listens*_ to the custom _doTest_ event fired by the Question component and executes the _speedTest()_ method callback
      * @event doTest
      * @type {Event}
      */
      speedTest () {
        const imageAddr = this.staticUrl + '/images/troubleshooter/homepagedownload.jpg' + '?n=' + Math.random()
        const downloadSize = 240626

        let startTime, endTime
        const download = new Image()

        startTime = (new Date()).getTime()
        download.src = imageAddr

        download.addEventListener('load', () => {
            endTime = (new Date()).getTime()
            const duration = (endTime - startTime) / 1000
            const bitsLoaded = downloadSize * 8
            const speedBps = (bitsLoaded / duration).toFixed(2)
            const speedKbps = (speedBps / 1024).toFixed(2)

            if (speedKbps > 30) {
              this.connectionSpeedPass = true
            } else {
              this.failTest(this.statusMessage({'desc': 'Network Connection Speed is too slow: ' + speedKbps + ' Kbps'}))
            }
        })
      },

      setUpSocketHandlers () {
        this.$options.sockets.onerror = (conn) => {
          if (conn.currentTarget.url.indexOf('vcapiws') > -1) {
            this.awsSocketPass = false
          }
          if (conn.currentTarget.url.indexOf('agora') > -1) {
            this.agoraSocketPass = false
          }
          if (conn.currentTarget.url.indexOf('twilio') > -1) {
            this.twilioSocketPass = false
          }
          this.showLoader = false
          this.failTest(this.statusMessage({'desc': 'Could not connect to socket server: ' + conn.currentTarget.url}))
        }
        this.$options.sockets.onopen = (conn) => {
          if (conn.currentTarget.url.indexOf('vcapiws') > -1) {
            this.awsSocketPass = true
          }
          if (conn.currentTarget.url.indexOf('agora') > -1) {
            this.agoraSocketPass = true
          }
          if (conn.currentTarget.url.indexOf('twilio') > -1) {
            this.twilioSocketPass = true
          }
          // this.$disconnect(conn.currentTarget.url)
        }
      },

      awsSocketConnectionTest () {
        this.$connect()
      },

      twilioSocketConnectionTest () {
        this.$connect('wss://global.vss.twilio.com/signaling')
        this.$connect('wss://sdkgw.us1.twilio.com/v1/VideoEvents')
      },

      async agoraSocketConnectionTest () {
        const rId = '23456543234'
        const uId = '1234321'
        const resp = await this.getToken(rId, uId)

        this.client.join(this.options.appId, rId, resp.data.token, uId)

        const checkSockets = () => {
          setTimeout(() => {
            if (this.client._joinInfo) {
              this.client._joinInfo.gatewayAddrs.forEach((addr) => {
                this.$connect(`wss://${addr.address}`)
              })
            } else {
              checkSockets()
            }
          }, 5000)
        }
        checkSockets()
      },

      startTests () {
        this.showLoader = true
        this.setUpSocketHandlers()
        this.speedTest()
        this.awsSocketConnectionTest()
        this.twilioSocketConnectionTest()
        this.agoraSocketConnectionTest()
      }
		}
	}

</script>

<style lang='less'>

</style>
